import { useState, useEffect } from "react";

import CloseIcon from "@mui/icons-material/Close";

/* ✅ single source of truth for spacing */
const containerClass = "mx-auto w-full max-w-[650px] px-6 sm:px-8 lg:px-10";

export default function CouponsOffcanvas({ onClose, onApply }) {
  const [activeFilter, setActiveFilter] = useState("All");
  const [openCard, setOpenCard] = useState(null);

  useEffect(() => {
    // lock background scroll
    const originalStyle = window.getComputedStyle(document.body).overflow;
    document.body.style.overflow = "hidden";

    return () => {
      // restore scroll when offcanvas closes
      document.body.style.overflow = originalStyle;
    };
  }, []);

  return (
    <>
      {/* BACKDROP */}
      <div
        className="fixed inset-0 bg-[#113b1880] backdrop-blur-sm z-40"
        onClick={onClose}
      />

      {/* OFFCANVAS */}
      <div
        className="
          fixed right-0 top-0 h-full
          md:w-full lg:w-[50%] xl:w-[40%]
          bg-[#f5f6f1] z-50 animate-slideIn
        "
      >
        <div className="h-full flex flex-col">
          {/* HEADER */}
          <div className="bg-white border-b py-4">
            <div
              className={`${containerClass} flex items-center justify-between`}
            >
              <h3 className="font-lexend font-semibold text-[22px]">
                Coupons & Offers
              </h3>
              <button
                onClick={onClose}
                className="w-6 h-6 flex items-center justify-center rounded-full border border-[#29562f] text-[#29562f]"
              >
                <CloseIcon sx={{ fontSize: 16 }} />
              </button>
            </div>
          </div>

          {/* INPUT */}
          <div className="bg-white border-b py-6">
            <div className={containerClass}>
              <input
                placeholder="Enter Coupon Code"
                className="
                  w-full border rounded-lg px-4 py-3
                  font-lexend text-[16px]
                  focus:outline-none
                  focus:border-[#2f5d3a]
                  focus:ring-1 focus:ring-[#2f5d3a]
                "
              />
            </div>
          </div>

          {/* FILTERS */}
          <div className="py-4">
            <div className={`${containerClass} flex gap-3 overflow-x-auto`}>
              {["All", "Credit Card", "EMI", "UPI"].map((t) => {
                const isActive = activeFilter === t;
                return (
                  <button
                    key={t}
                    onClick={() => setActiveFilter(t)}
                    className={`
                      px-4 py-2 rounded-full text-[14px] font-lexend whitespace-nowrap
                      border transition-all
                      ${
                        isActive
                          ? "bg-[#2f5d3a] text-[#f4e391] border-[#2f5d3a]"
                          : "bg-white text-[#333] border-[#cfcfcf] hover:bg-[#2f5d3a] hover:text-white"
                      }
                    `}
                  >
                    {t}
                  </button>
                );
              })}
            </div>
          </div>

          {/* COUPONS LIST */}
          <div className="flex-1 overflow-y-auto pb-10">
            <div className={`${containerClass} space-y-6`}>
              <CouponCard
                id="MOONPAY"
                openCard={openCard}
                setOpenCard={setOpenCard}
                code="MOONPAY"
                save="Save ₹6,174"
                desc="Use MOONPAY and get upto 65% discount"
                min="No Min Order"
                expiry="03 Feb 26"
                offer="Upto 65% Off"
                onApply={onApply}
                onClose={onClose}
              />

              <CouponCard
                id="MOONUPI"
                openCard={openCard}
                setOpenCard={setOpenCard}
                code="MOONUPI"
                save="Save ₹6,174"
                desc="Use MOONUPI and get upto 65% discount"
                min="No Min Order"
                expiry="03 Feb 26"
                offer="Upto 65% Off"
                onApply={onApply}
                onClose={onClose}
              />

              <CouponCard
                id="MOONCARD"
                openCard={openCard}
                setOpenCard={setOpenCard}
                code="MOONCARD"
                save="Save ₹6,174"
                desc="Use MOONCARD and get upto 65% discount"
                min="No Min Order"
                expiry="03 Feb 26"
                offer="Upto 65% Off"
                onApply={onApply}
                onClose={onClose}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

/* ================= CARD ================= */

function CouponCard({
  id,
  openCard,
  setOpenCard,
  code,
  save,
  desc,
  min,
  expiry,
  offer,
  onApply,
  onClose,
}) {
  const isOpen = openCard === id;

  return (
    <div className="bg-white rounded-2xl p-5 shadow-sm relative overflow-hidden border border-transparent hover:border-[#2f5d3a] transition">
      {/* OFFER PILL */}
      <span
        className="
          absolute top-0 right-0
          bg-[#2f5d3a]
          text-[#f4e391]
          text-[12px] font-medium
          px-4 py-1.5
          rounded-bl-xl
          font-lexend
        "
      >
        {offer}
      </span>

      {/* CODE */}
      <p className="font-lexend font-semibold text-[18px] text-[#333]">
        {code}
      </p>
      <p className="text-[#2f5d3a] font-lexend text-[14px]">{save}</p>

      {/* DASHED LINE */}
      <div className="border-t border-dashed border-[#2f5d3a] my-4" />

      {/* DESCRIPTION */}
      <p className="font-lexend text-[15px] text-[#333]">{desc}</p>

      {/* META */}
      <p className="font-lexend text-[13px] text-[#a7a7a5] mt-2">
        Min Order : {min} &nbsp; | &nbsp; Expires on : {expiry}
      </p>

      {/* FOOTER */}
      <div className="flex justify-between items-center mt-4">
        <button
          onClick={() => setOpenCard(isOpen ? null : id)}
          className="flex items-center gap-1 text-[#333] font-lexend text-[14px]"
        >
          Details
          <span className="text-[12px]">{isOpen ? "▲" : "▼"}</span>
        </button>

        <button
          className="
            border border-[#2f5d3a]
            text-[#2f5d3a]
            px-6 py-2
            rounded-lg
            font-lexend text-[14px]
            hover:bg-[#2f5d3a] hover:text-white
          "
          onClick={() => {
            const discount = parseInt(save.split("₹")[1].replace(",", ""));
            onApply({ code, discount });
            onClose();
          }}
        >
          Apply
        </button>
      </div>

      {/* EXPANDED DETAILS */}
      {isOpen && (
        <div className="mt-5 pt-4 border-t border-[#e6e6e6]">
          <p className="font-lexend font-semibold text-[15px] mb-2">
            Terms & Conditions
          </p>

          <ul className="list-decimal pl-5 space-y-1 font-lexend text-[14px] text-[#555]">
            <li>
              Use <span className="font-semibold">{code}</span> code.
            </li>
            <li>Overall discount up to 65%.</li>
            <li>Valid on select Moondew products.</li>
            <li>Cannot be combined with other offers.</li>
            <li>Valid till {expiry}.</li>
          </ul>
        </div>
      )}
    </div>
  );
}